import React from 'react';
import moment from 'moment';
import NumberFormat from 'react-number-format';

import { CURRENCY } from '../../../../../shared/enums/currency.enum';
import { PaymentGateway } from '../../../../../shared/enums/payment-gateway.enum';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { PaymentModel } from '../../../../../shared/models/payment.model';
import { paymentTypeOptions } from '../../reservation-edit.helper';
import { PaymentActionsDropdown } from './payment-actions-dropdown.component';
import { PaymentStatusBadge } from './payment-status-badge.component';

interface PaymentsTableProps {
  payments: PaymentModel[];
  reservationSource?: string;
  isPermited: (roles: UserRole[]) => boolean;
  checkingPaymobPaymentId: string;
  switchingGatewayPaymentId: string;
  onViewDetails: (payment: PaymentModel) => void;
  onSend: (payment: PaymentModel) => void;
  onPartial: (payment: PaymentModel) => void;
  onSplitPayment: (payment: PaymentModel) => void;
  onGenerateLink: (payment: PaymentModel) => void;
  onAddReceipt: (payment: PaymentModel) => void;
  onViewHistory: (payment: PaymentModel) => void;
  onViewVatBreakdown: (payment: PaymentModel) => void;
  onResendConfirmation: (payment: PaymentModel) => void;
  onCheckPaymobStatus: (payment: PaymentModel) => void;
  onSwitchGateway: (payment: PaymentModel, targetGateway: PaymentGateway) => void;
}

function getPaymentMethod(payment: PaymentModel): string {
  if (payment.attempts && payment.attempts.length > 0) {
    for (let index = payment.attempts.length - 1; index >= 0; index--) {
      const attempt = payment.attempts[index];
      if (attempt.cardNumber) {
        return attempt.cardNumber;
      }
    }
  }

  const typeOption = paymentTypeOptions.find((option) => option.value === payment.paymentType);
  return typeOption ? typeOption.label : payment.paymentType || 'N/A';
}

function getPaymentGateway(payment: PaymentModel): string {
  if (payment.paymentGateway === 'paymob') {
    return 'Paymob';
  }
  if (payment.paymentGateway === 'geidea') {
    return 'Geidea';
  }
  return 'N/A';
}

function getDescription(payment: PaymentModel): string {
  if (payment.paymentItems && payment.paymentItems.length > 0) {
    const names = payment.paymentItems.filter((item) => item.feeName).map((item) => item.feeName);
    if (names.length > 0) {
      return names.length > 2 ? `${names[0]}, ${names[1]}...` : names.join(', ');
    }
  }
  return payment.direction === 'out' ? 'Refund' : 'Payment';
}

function getPaidDate(payment: PaymentModel): string {
  if (payment.collectedAt) {
    return moment(payment.collectedAt).format('D MMM, YYYY');
  }
  if (payment.dueDate) {
    return moment(payment.dueDate).format('D MMM, YYYY');
  }
  return 'N/A';
}

function getCurrencyPrefix(currency?: string): string {
  return currency ? `${CURRENCY[currency as keyof typeof CURRENCY]} ` : 'EGP ';
}

function getPaymentRowBackground(payment: PaymentModel): string {
  if (payment.direction === 'out') {
    return '#fffbeb';
  }
  if (payment.paymentAmount <= 0) {
    return '#f9fafb';
  }
  return 'white';
}

const headerStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#6B7280',
  padding: '14px 16px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderBottom: '2px solid #e5e7eb'
};

const cellStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#111827',
  padding: '16px',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'middle'
};

export function PaymentsTable({
  payments,
  reservationSource,
  isPermited,
  checkingPaymobPaymentId,
  switchingGatewayPaymentId,
  onViewDetails,
  onSend,
  onPartial,
  onSplitPayment,
  onGenerateLink,
  onAddReceipt,
  onViewHistory,
  onViewVatBreakdown,
  onResendConfirmation,
  onCheckPaymobStatus,
  onSwitchGateway
}: PaymentsTableProps): JSX.Element {
  return payments.length > 0 ? (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden'
      }}
    >
      {/* Left green accent bar */}
      <div style={{ position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            backgroundColor: '#22c55e',
            borderTopLeftRadius: '12px',
            borderBottomLeftRadius: '12px',
            zIndex: 1
          }}
        />
        <div
          style={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <table
            style={{
              width: '100%',
              minWidth: '820px',
              borderCollapse: 'collapse',
              tableLayout: 'auto'
            }}
          >
            <thead>
              <tr style={{ backgroundColor: '#fafafa' }}>
                <th style={{ ...headerStyle, paddingLeft: '24px' }}>ID</th>
                <th style={headerStyle}>Payment method</th>
                <th style={headerStyle}>Payment gateway</th>
                <th style={headerStyle}>Amount</th>
                <th style={headerStyle}>Direction</th>
                <th style={headerStyle}>Status</th>
                <th style={headerStyle}>Description</th>
                <th style={headerStyle}>Paid on</th>
                <th style={{ ...headerStyle, textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, index) => (
                <tr
                  key={payment.id || index}
                  onClick={() => onViewDetails(payment)}
                  style={{
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                    backgroundColor: getPaymentRowBackground(payment)
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = getPaymentRowBackground(payment);
                  }}
                >
                  {/* ID */}
                  <td
                    style={{
                      ...cellStyle,
                      paddingLeft: '24px',
                      fontWeight: 500,
                      color: '#374151',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {payment.id?.length > 10 ? payment.id.substring(0, 10) + '...' : payment.id}
                  </td>

                  {/* Payment method */}
                  <td style={{ ...cellStyle, fontWeight: 600, color: '#111827' }}>
                    {getPaymentMethod(payment)}
                  </td>

                  <td style={{ ...cellStyle, fontWeight: 600, color: '#111827' }}>
                    {getPaymentGateway(payment)}
                  </td>

                  {/* Amount */}
                  <td
                    style={{
                      ...cellStyle,
                      fontWeight: 500,
                      color: payment.paymentAmount <= 0 ? '#EF4444' : '#111827'
                    }}
                  >
                    <NumberFormat
                      value={payment.paymentAmount || 0}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={getCurrencyPrefix(payment.currency)}
                    />
                  </td>

                  {/* Direction */}
                  <td style={cellStyle}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 12px',
                        borderRadius: '9999px',
                        fontSize: '13px',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        backgroundColor: payment.direction === 'out' ? '#fffbeb' : '#ecfdf3',
                        color: payment.direction === 'out' ? '#D97706' : '#06A458',
                        border: `1px solid ${payment.direction === 'out' ? '#fde68a' : '#a6f4c5'}`
                      }}
                    >
                      {payment.direction === 'out' ? 'PAY-OUT' : 'PAY-IN'}
                    </span>
                  </td>

                  {/* Status */}
                  <td style={cellStyle}>
                    <PaymentStatusBadge status={payment.status} />
                  </td>

                  {/* Description */}
                  <td
                    style={{
                      ...cellStyle,
                      color: '#374151',
                      maxWidth: '160px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {getDescription(payment)}
                  </td>

                  {/* Paid on */}
                  <td style={{ ...cellStyle, color: '#374151' }}>{getPaidDate(payment)}</td>

                  {/* Action */}
                  <td
                    style={{ ...cellStyle, textAlign: 'center' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PaymentActionsDropdown
                      payment={payment}
                      isPermited={isPermited}
                      reservationSource={reservationSource}
                      isCheckingPaymobStatus={checkingPaymobPaymentId === payment.id}
                      isSwitchingGateway={switchingGatewayPaymentId === payment.id}
                      onSend={() => onSend(payment)}
                      onPartial={() => onPartial(payment)}
                      onSplitPayment={() => onSplitPayment(payment)}
                      onGenerateLink={() => onGenerateLink(payment)}
                      onAddReceipt={() => onAddReceipt(payment)}
                      onViewHistory={() => onViewHistory(payment)}
                      onViewVatBreakdown={() => onViewVatBreakdown(payment)}
                      onResendConfirmation={() => onResendConfirmation(payment)}
                      onCheckPaymobStatus={() => onCheckPaymobStatus(payment)}
                      onSwitchGateway={(targetGateway) => onSwitchGateway(payment, targetGateway)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  ) : (
    <div className="p-10 text-center text-sm rounded-xl text-gray-500 border border-dashed border-neutral-[#e5e7eb]">
      No payments found
    </div>
  );
}
